/**
 * W8 non-component logic — the version timeline, the AFE-authorization rollup
 * (D24: authorized_total = original + Σ approved supplements), and the version
 * diff (field-level comparison of two plan versions' design datasets).
 *
 * Every function tolerates a present-but-non-array API body: the version list,
 * the supplements list, and each dataset are coerced with `asArray<T>` at the
 * boundary, so a real backend returning `{}` / `{versions:null}` / an error
 * envelope yields an empty result — never a `.map`/`.reduce` white-screen
 * (the Oxbo-Z prod crash class). See [[../../lib/asArray]].
 */
import type {
  PlanOut,
  PlanDatasets,
  SupplementOut,
  ProgramaStep,
  AfeLineRow,
  AttributeRow,
} from "@api/types";
import { asArray } from "@lib/asArray";

// ───────────────────────────── Version timeline ─────────────────────────────

/** Versions newest-first (v4, v3, …) — the timeline order the wireframe shows. */
export function versionList(plans: unknown): PlanOut[] {
  return [...asArray<PlanOut>(plans)].sort((a, b) => b.version - a.version);
}

/** The current (highest-version) plan, or null. */
export function currentPlan(plans: unknown): PlanOut | null {
  return versionList(plans)[0] ?? null;
}

/** The version immediately below `version` (its diff baseline), or null. */
export function priorVersion(plans: unknown, version: number): PlanOut | null {
  return (
    versionList(plans)
      .filter((p) => p.version < version)
      .sort((a, b) => b.version - a.version)[0] ?? null
  );
}

// ───────────────────────────── AFE supplement rollup (D24) ──────────────────

export interface SupplementRollup {
  /** Original AFE (the plan's authorized_total minus already-approved supps). */
  original: number;
  /** Σ of approved supplement amounts (the delta that has been authorized). */
  approvedTotal: number;
  /** Σ of still-in-flight (requested) supplement amounts — informational only. */
  pendingTotal: number;
  approvedCount: number;
  pendingCount: number;
  rejectedCount: number;
  /** Authorized total in force = original + approvedTotal. */
  authorizedTotal: number;
}

/**
 * Roll a plan's supplements up to the authorized total. The plan's
 * `authorized_total` already includes approved supplements (server-computed),
 * so the ORIGINAL AFE is that figure minus Σ approved. When the plan carries no
 * total we fall back to Σ approved as the authorized figure.
 */
export function supplementRollup(
  plan: PlanOut | null | undefined,
  supplements: unknown,
): SupplementRollup {
  const supps = asArray<SupplementOut>(supplements);
  let approvedTotal = 0;
  let pendingTotal = 0;
  let approvedCount = 0;
  let pendingCount = 0;
  let rejectedCount = 0;
  for (const s of supps) {
    if (s.status === "approved") {
      approvedTotal += s.amount ?? 0;
      approvedCount += 1;
    } else if (s.status === "requested") {
      pendingTotal += s.amount ?? 0;
      pendingCount += 1;
    } else if (s.status === "rejected") {
      rejectedCount += 1;
    }
  }
  const total = plan?.authorized_total ?? null;
  const original = total != null ? total - approvedTotal : 0;
  const authorizedTotal = total ?? approvedTotal;
  return {
    original,
    approvedTotal,
    pendingTotal,
    approvedCount,
    pendingCount,
    rejectedCount,
    authorizedTotal,
  };
}

/** Projected authorized total if the given in-flight amount were approved. */
export function projectedTotal(rollup: SupplementRollup, addAmount: number): number {
  const add = Number.isFinite(addAmount) ? addAmount : 0;
  return rollup.authorizedTotal + Math.max(0, add);
}

// ───────────────────────────── Version diff ─────────────────────────────────

/** One field-level change between two versions, grouped by dataset. */
export interface DiffRow {
  /** i18n key for the row label (or a raw label when the field is data-driven). */
  label: string;
  /** True when `label` is a literal (dataset-derived) rather than an i18n key. */
  rawLabel?: boolean;
  before: string | null;
  after: string | null;
  changed: boolean;
}
export interface DiffGroup {
  /** i18n key for the group heading (programa / costo / targets). */
  titleKey: string;
  rows: DiffRow[];
}

/** Sum a program's phases (guards a non-array `programa`). */
function programSummary(ds: PlanDatasets | undefined) {
  const rows = asArray<ProgramaStep>(ds?.programa);
  const phases = rows.length;
  const days = rows.reduce((a, r) => a + (r.dol ?? 0), 0);
  return { phases, days };
}

/** Total budget across afe lines (guards a non-array `afe_lines`). */
function afeTotal(ds: PlanDatasets | undefined): number {
  return asArray<AfeLineRow>(ds?.afe_lines).reduce((a, r) => a + (r.budget ?? 0), 0);
}

/** Map a target-attribute name → value (guards a non-array `objetivo`). */
function objetivoMap(ds: PlanDatasets | undefined): Map<string, string> {
  const m = new Map<string, string>();
  for (const r of asArray<AttributeRow>(ds?.objetivo)) {
    m.set(r.name, r.unit ? `${r.value} ${r.unit}` : r.value);
  }
  return m;
}

function fmtOrNull(n: number, fmt: (n: number) => string): string {
  return fmt(n);
}

/**
 * Build the grouped, field-level diff between a version and its prior version.
 * `fmtNum` / `fmtMoney` are injected so the model stays locale-agnostic.
 * With no prior version (v1) every "before" is null and nothing is marked
 * changed — the caller shows an "initial version" note instead.
 */
export function buildDiff(
  after: PlanDatasets | undefined,
  before: PlanDatasets | undefined,
  fmt: { num: (n: number) => string; money: (n: number) => string },
): DiffGroup[] {
  const hasBefore = before != null;
  const aProg = programSummary(after);
  const bProg = programSummary(before);
  const aAfe = afeTotal(after);
  const bAfe = afeTotal(before);
  const aObj = objetivoMap(after);
  const bObj = objetivoMap(before);

  const num = (n: number) => fmtOrNull(n, fmt.num);
  const money = (n: number) => fmtOrNull(n, fmt.money);

  const programa: DiffGroup = {
    titleKey: "versions.diff.programa",
    rows: [
      {
        label: "versions.diff.phases",
        before: hasBefore ? num(bProg.phases) : null,
        after: num(aProg.phases),
        changed: hasBefore && aProg.phases !== bProg.phases,
      },
      {
        label: "versions.diff.days",
        before: hasBefore ? num(bProg.days) : null,
        after: num(aProg.days),
        changed: hasBefore && aProg.days !== bProg.days,
      },
    ],
  };

  const costo: DiffGroup = {
    titleKey: "versions.diff.costo",
    rows: [
      {
        label: "versions.diff.afeTotal",
        before: hasBefore ? money(bAfe) : null,
        after: money(aAfe),
        changed: hasBefore && aAfe !== bAfe,
      },
    ],
  };

  // Targets: union of both versions' objetivo attribute names, in after-then-
  // before order, so an added / removed / changed target all surface.
  const names: string[] = [];
  for (const k of aObj.keys()) if (!names.includes(k)) names.push(k);
  for (const k of bObj.keys()) if (!names.includes(k)) names.push(k);
  const targets: DiffGroup = {
    titleKey: "versions.diff.targets",
    rows: names.map((name) => {
      const b = bObj.get(name) ?? null;
      const a = aObj.get(name) ?? null;
      return {
        label: name,
        rawLabel: true,
        before: hasBefore ? b : null,
        after: a,
        changed: hasBefore && a !== b,
      };
    }),
  };

  return [programa, costo, targets].filter((g) => g.rows.length > 0);
}

// ───────────────────────────── Backend rules (D1 / D24) ─────────────────────

/** A plan may be published only once it reaches approved / locked. */
export function canPublish(status: PlanOut["status"] | undefined): boolean {
  return status === "approved" || status === "locked" || status === "published";
}

/** A locked (or published) plan rejects new supplement requests. */
export function acceptsSupplements(status: PlanOut["status"] | undefined): boolean {
  return status !== "locked" && status !== "published";
}
